// BoothManagement.js: Admin page for managing event booths
import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Button, Table, TableHead, TableRow, TableCell, TableBody, IconButton } from '@mui/material';
import BoothAssignModal from '../../components/BoothAssignModal';
import StorefrontIcon from '@mui/icons-material/Storefront';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SuperAdminLayout from '../../layouts/SuperAdminLayout';
import api from '../../services/api';
import NotificationSnackbar from '../../components/NotificationSnackbar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';


function BoothManagement() {
  const [booths, setBooths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', location: '', size: '', positionDetails: '', inclusions: '', contact: '' });
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedBooth, setSelectedBooth] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get('/api/booths')
      .then(res => setBooths(res.data))
      .catch(err => setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to fetch booths', severity: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  const handleAddBooth = () => setAddOpen(true);
  const handleAddClose = () => {
    setAddOpen(false);
    setAddForm({ name: '', location: '', size: '', positionDetails: '', inclusions: '', contact: '' });
  };
  const handleAddFormChange = (e) => {
    setAddForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };
  const handleAddSubmit = async () => {
    if (!addForm.name || !addForm.location || !addForm.size) {
      setSnackbar({ open: true, message: 'Name, location, and size are required', severity: 'error' });
      return;
    }
    setLoading(true);
    setSnackbar({ open: false, message: '', severity: 'info' });
    try {
      const payload = {
        ...addForm,
        inclusions: addForm.inclusions ? addForm.inclusions.split(',').map(i => i.trim()) : [],
      };
      const res = await api.post('/api/booths', payload);
      setBooths(prev => [...prev, res.data]);
      setSnackbar({ open: true, message: 'Booth added successfully', severity: 'success' });
      handleAddClose();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Add booth failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async (id) => {
    setLoading(true);
    setSnackbar({ open: false, message: '', severity: 'info' });
    try {
      await api.delete(`/api/booths/${id}`);
      setBooths(prev => prev.filter(b => b._id !== id));
      setSnackbar({ open: true, message: 'Booth deleted successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Delete failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <SuperAdminLayout>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h4" fontWeight="bold" mb={2} color="primary">Booth Management</Typography>
        <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<StorefrontIcon />}
              sx={{ fontWeight: 600, borderRadius: 2 }}
              onClick={handleAddBooth}
            >
              Add Booth
            </Button>
            <Typography variant="body2" color="text.secondary">Manage all event booths, bookings, and contacts.</Typography>
          </Box>
          <NotificationSnackbar
            open={snackbar.open}
            onClose={() => setSnackbar(s => ({ ...s, open: false }))}
            message={snackbar.message}
            severity={snackbar.severity}
          />
          {loading ? (
            <Typography>Loading...</Typography>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Position</TableCell>
                  <TableCell>Inclusions</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Assigned Booker</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {booths.map((booth) => (
                  <TableRow key={booth._id}>
                    <TableCell>{booth.name}</TableCell>
                    <TableCell>{booth.location}</TableCell>
                    <TableCell>{booth.size}</TableCell>
                    <TableCell>{booth.positionDetails}</TableCell>
                    <TableCell>{booth.inclusions?.join(', ')}</TableCell>
                    <TableCell>
                      {booth.assignedBooker && booth.status === 'pending' ? 'Pending Verification' :
                        booth.assignedBooker && booth.status === 'verified' ? 'Verified' :
                        booth.assignedBooker ? 'Assigned' : 'Unassigned'}
                    </TableCell>
                    <TableCell>{booth.contact}</TableCell>
                    <TableCell>{booth.assignedBooker ? 'Assigned' : 'Unassigned'}</TableCell>
                    <TableCell align="right">
                      <IconButton color="primary" size="small" onClick={() => { setSelectedBooth(booth); setAssignOpen(true); }}>Assign</IconButton>
                      {/* Only super admin/admin can verify */}
                      {booth.assignedBooker && booth.status === 'pending' && (
                        <Button color="success" size="small" onClick={async () => {
                          setLoading(true);
                          try {
                            await api.post(`/api/booths/${booth._id}/verify`);
                            setSnackbar({ open: true, message: 'Booth verified!', severity: 'success' });
                            api.get('/api/booths').then(res => setBooths(res.data));
                          } catch (err) {
                            setSnackbar({ open: true, message: err.response?.data?.error || 'Verification failed', severity: 'error' });
                          } finally {
                            setLoading(false);
                          }
                        }}>Verify</Button>
                      )}
                      <IconButton color="error" size="small" onClick={() => handleDelete(booth._id)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Dialog open={addOpen} onClose={handleAddClose}>
            <DialogTitle>Add Booth</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                name="name"
                label="Booth Name"
                fullWidth
                value={addForm.name}
                onChange={handleAddFormChange}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                name="location"
                label="Location"
                fullWidth
                value={addForm.location}
                onChange={handleAddFormChange}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                name="size"
                label="Size"
                fullWidth
                value={addForm.size}
                onChange={handleAddFormChange}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                name="positionDetails"
                label="Position Details"
                fullWidth
                value={addForm.positionDetails}
                onChange={handleAddFormChange}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                name="inclusions"
                label="Inclusions (comma separated)"
                fullWidth
                value={addForm.inclusions}
                onChange={handleAddFormChange}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                name="contact"
                label="Contact"
                fullWidth
                value={addForm.contact}
                onChange={handleAddFormChange}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleAddClose}>Cancel</Button>
              <Button onClick={handleAddSubmit} variant="contained">Add</Button>
            </DialogActions>
          </Dialog>
          <BoothAssignModal
            open={assignOpen}
            onClose={() => { setAssignOpen(false); setSelectedBooth(null); }}
            booth={selectedBooth}
            onAssigned={() => {
              setSnackbar({ open: true, message: 'Booth assigned successfully', severity: 'success' });
              setAssignOpen(false);
              setSelectedBooth(null);
              // Optionally, refresh booths
              api.get('/api/booths').then(res => setBooths(res.data));
            }}
          />
        </Paper>
      </Box>
    </SuperAdminLayout>
  );
}

export default BoothManagement;
